import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [attempts, setAttempts] = useState(0);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          // Refresh user profile to pick up new tier
          try {
            const me = await api.get("/auth/me");
            localStorage.setItem("gf_user", JSON.stringify(me.data));
            setUser(me.data);
          } catch (err) {
            // Payment succeeded but the /auth/me refresh failed — the new tier will
            // be picked up on the next page load. Surface a light toast so the user
            // knows to reload if they see stale state.
            if (err?.response?.status !== 401) {
              toast.info("Payment confirmed. Refresh if the plan doesn't show up.");
            }
          }
        } else if (["expired", "failed"].includes(res.data.payment_status)) {
          setStatus("failed");
        } else {
          setAttempts((n) => n + 1);
          if (attempts < 30) setTimeout(poll, 2000);
          else setStatus("timeout");
        }
      } catch {
        setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, attempts, setUser]);

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="max-w-md w-full bg-white border border-[#E6E4DF] rounded-lg p-10 text-center">
        {status === "polling" && (
          <>
            <Loader2 className="w-10 h-10 text-[#1C3F35] mx-auto animate-spin"/>
            <h1 className="font-heading text-3xl mt-6">Confirming payment…</h1>
            <p className="text-[#5E5E5E] mt-2 text-sm">Hang tight, this takes a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-14 h-14 rounded-full bg-[#1C3F35] mx-auto flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-white"/>
            </div>
            <h1 className="font-heading text-4xl mt-6 font-light tracking-tight">You're in.</h1>
            <p className="text-[#5E5E5E] mt-2">Your subscription is active. Let's go win some grants.</p>
            <Button
              onClick={()=>navigate("/dashboard")}
              data-testid="success-cta"
              className="mt-8 bg-[#1C3F35] text-white hover:bg-[#1A362D] w-full py-6"
            >
              Open dashboard
            </Button>
          </>
        )}
        {(status === "failed" || status === "timeout" || status === "error") && (
          <>
            <h1 className="font-heading text-3xl">Something didn't complete.</h1>
            <p className="text-[#5E5E5E] mt-2 text-sm">Payment status: {status}. If money was charged and this persists, contact support.</p>
            <Link to="/pricing" className="block mt-8 text-[#1C3F35] underline">Back to pricing</Link>
          </>
        )}
      </div>
    </div>
  );
}
